import React, { useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { showWhatsNew } from '@/components/WhatsNewModal';
import { Input } from '@/components/ui/input';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from '@/components/ui/accordion';
import {
  HelpCircle, User, Trophy, Building2, Search, Mail, ExternalLink,
  Sparkles, MessageSquare
} from 'lucide-react';

const SECTIONS = [
  {
    id: 'players',
    label: 'Giocatori',
    icon: User,
    accent: 'from-blue-600 via-indigo-600 to-violet-600',
    blurb: 'Iscriverti, partecipare a partite, leggere statistiche.',
    faqs: [
      {
        q: 'Come mi iscrivo a una lega?',
        a: 'Apri "Leghe" dal menu, trova quella che ti interessa e premi Unisciti. Se la lega è privata, ti serve il codice di invito che ti dà l\'organizzatore.',
      },
      {
        q: 'Come partecipo a una partita?',
        a: 'Vai alla pagina della partita e premi "Iscriviti come Titolare". Se i posti da titolare sono pieni, puoi entrare in lista riserve: subentri automaticamente se qualcuno lascia.',
      },
      {
        q: 'Come funzionano le riserve?',
        a: 'Quando i titolari sono al completo, puoi iscriverti come riserva. Il numero accanto al tuo nome indica la tua posizione in coda. Quando un titolare si ritira, la prima riserva diventa titolare in automatico.',
      },
      {
        q: 'Dove vedo le mie statistiche?',
        a: 'Apri il tuo Profilo: trovi vittorie, pareggi, sconfitte, win rate e gol fatti. La pagina della lega mostra invece la classifica generale con podio.',
      },
      {
        q: 'Posso scegliere un soprannome?',
        a: 'Sì. Vai su Profilo → tab Giocatore → Soprannome. Verrà mostrato accanto al tuo nome ovunque.',
      },
      {
        q: 'A chi sono visibili i miei dati?',
        a: 'Tu controlli tutto da Profilo → Privacy. Puoi nascondere email, telefono, città, statistiche e disponibilità con un singolo switch.',
      },
      {
        q: 'Come trovo partite vicine a me?',
        a: 'Usa "Cerca" per filtrare partite per sport e città. Solo le partite pubbliche compaiono qui. Le partite di una lega privata si vedono solo se sei membro.',
      },
      {
        q: 'Cosa cambia tra una partita e una competizione?',
        a: 'Una partita è un singolo match. Una competizione è un torneo o un campionato che raggruppa più partite con squadre, classifica e tabellone.',
      },
      {
        q: 'Come ricevo le notifiche delle nuove partite?',
        a: 'Quando viene creata una partita nella tua lega ricevi una notifica nella campanella in alto a destra. Per riceverla anche come push sul telefono o sul browser apri il campanellino e premi "Attiva" la prima volta: il browser ti chiederà il permesso. Su iPhone devi prima aggiungere LeagueHub alla schermata Home (banner "Installa l\'app") perché Apple permette le push solo dalle PWA installate.',
      },
      {
        q: 'Le notifiche push non mi arrivano, perché?',
        a: 'Tre cause comuni: 1) non hai mai dato il permesso (vai al campanellino → "Attiva"); 2) le hai bloccate prima — riapri le impostazioni del browser per il sito leaguehub.it e rimetti il permesso su "Consenti"; 3) sei su iPhone ma non hai installato l\'app come PWA dalla schermata Home.',
      },
    ],
  },
  {
    id: 'admins',
    label: 'Admin lega',
    icon: Trophy,
    accent: 'from-amber-500 via-orange-500 to-rose-500',
    blurb: 'Creare leghe, gestire membri, regole di punteggio e import.',
    faqs: [
      {
        q: 'Come creo una lega?',
        a: 'Dal menu "Leghe" premi "Nuova lega". Scegli sport, nome, descrizione e copertina. Puoi farla pubblica (chiunque può iscriversi) o privata (serve il codice di invito).',
      },
      {
        q: 'Come invito i giocatori?',
        a: 'Dalla pagina della lega copia il codice di invito (sezione "Invita giocatori"). Funziona per leghe pubbliche e private. Dal pulsante condividi di una partita puoi mandare il link su WhatsApp.',
      },
      {
        q: 'Come gestisco i membri?',
        a: 'Apri la lega, tab "Membri". Puoi promuovere/declassare manager, rimuovere giocatori, e collegare account "placeholder" (giocatori senza account vero) a utenti reali quando si iscrivono.',
      },
      {
        q: 'Come funziona il punteggio personalizzato?',
        a: 'Tab "Scoring" della lega: imposti i punti per Vittoria, Pareggio, Sconfitta. Il default è V=3, P=1, S=1. Puoi anche fare correzioni manuali per casi speciali (penalità, bonus).',
      },
      {
        q: 'Posso importare giocatori da Excel?',
        a: 'Sì, dal tab "Import" della lega. Carichi un file .xlsx o .csv con i nomi (e opzionalmente email/punti pregressi). Vedi un\'anteprima editabile prima di confermare l\'import.',
      },
      {
        q: 'Come creo un torneo?',
        a: 'Dalla pagina della lega premi "Nuova competizione", scegli "Torneo" e il formato (eliminazione diretta, gironi, ecc). Crea le squadre, poi aggiungi le partite. Il tabellone si genera automaticamente.',
      },
      {
        q: 'Come faccio un reset stagione?',
        a: 'Tab "Gestione" → "Reset stagione". Salva classifica finale e partite nello storico consultabile dalla tab "Classifica", poi avvia la nuova stagione con punteggi azzerati.',
      },
      {
        q: 'Come pubblico un annuncio?',
        a: 'Dal menu "Annunci" → "Nuovo Annuncio". Scegli il tipo (cerco giocatori, partita, squadra) e la lega di riferimento.',
      },
      {
        q: 'Chi può generare automaticamente le squadre di una partita?',
        a: 'Tutti gli admin e i manager della lega, oltre a chi ha creato la partita. Apri il dettaglio partita → "Genera squadre": l\'algoritmo bilancia i giocatori confermati in due squadre tenendo conto del livello e dei ruoli. Le squadre proposte non vengono salvate finché non confermi.',
      },
      {
        q: 'Quando viene notificata una partita ai membri della lega?',
        a: 'La notifica in-app (e push, se attivate) parte appena la partita viene creata, anche se è in stato "bozza". Così tutti i membri della lega la vedono subito nel campanellino e possono iscriversi senza aspettare la pubblicazione.',
      },
    ],
  },
  {
    id: 'facilities',
    label: 'Strutture',
    icon: Building2,
    accent: 'from-emerald-600 via-teal-600 to-cyan-600',
    blurb: 'Registrare la tua struttura, accettare prenotazioni, ricevere pagamenti.',
    faqs: [
      {
        q: 'Come registro la mia struttura?',
        a: 'Vai su /business e premi "Registra la tua struttura". Compili i dati (nome, indirizzo, sport offerti, foto, IBAN per i pagamenti) e in pochi minuti sei online sulla mappa.',
      },
      {
        q: 'Quanto costa essere su LeagueHub?',
        a: 'L\'iscrizione base è gratuita. Trattieniamo una piccola commissione sulle prenotazioni pagate online. C\'è un piano Premium opzionale per analytics, calendario dedicato e visibilità extra.',
      },
      {
        q: 'Come gestisco prenotazioni e calendario?',
        a: 'Dalla Dashboard struttura: vedi prenotazioni in arrivo, calendario settimanale per campo, e puoi creare prenotazioni manuali per chi paga sul posto.',
      },
      {
        q: 'I clienti pagano tramite l\'app?',
        a: 'Sì, possono pagare con carta tramite Stripe Checkout. Puoi anche accettare prenotazioni con pagamento sul posto ("paga in struttura"): la prenotazione resta attiva fino a quando confermi il pagamento manualmente.',
      },
      {
        q: 'Quando ricevo i soldi delle prenotazioni online?',
        a: 'I pagamenti online arrivano accumulati sul tuo account LeagueHub. Dalla sezione "Pagamenti" della Dashboard puoi richiedere il payout sul tuo IBAN. Vedi le commissioni Stripe e l\'importo netto.',
      },
      {
        q: 'Posso collegare una lega alla mia struttura?',
        a: 'Sì. Quando una lega gioca da te, puoi associarla in modo che le prenotazioni delle partite si auto-allineino con il calendario campi.',
      },
      {
        q: 'Cosa cambia con il piano Premium?',
        a: 'Premium ti dà analytics avanzati (utilizzo per fascia oraria, lead generation), pagina pubblica con SEO migliorata, e priorità nella lista strutture quando un giocatore cerca un campo nella tua zona.',
      },
      {
        q: 'Come modifico orari, sport, o campi?',
        a: 'Dalla Dashboard struttura → tab "Campi" o "Impostazioni". Aggiungi/rimuovi campi, modifica orari di apertura, prezzi per slot, sport supportati. Le modifiche sono live subito.',
      },
    ],
  },
];

const QuickLink = ({ to, icon: Icon, label }) => (
  <Link to={to} className="block">
    <Card className="overflow-hidden hover:shadow-lg hover:-translate-y-0.5 transition-all">
      <CardContent className="p-4 flex items-center gap-3">
        <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-blue-500/15 to-indigo-500/15 flex items-center justify-center text-primary">
          <Icon className="h-5 w-5" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-medium truncate">{label}</p>
        </div>
        <ExternalLink className="h-4 w-4 text-muted-foreground" />
      </CardContent>
    </Card>
  </Link>
);

const SectionTab = ({ section, query }) => {
  const filtered = query
    ? section.faqs.filter(f =>
      f.q.toLowerCase().includes(query.toLowerCase()) ||
      f.a.toLowerCase().includes(query.toLowerCase())
    )
    : section.faqs;

  if (query && filtered.length === 0) {
    return (
      <Card>
        <CardContent className="p-8 text-center text-muted-foreground">
          <Search className="h-10 w-10 mx-auto mb-2 opacity-40" />
          <p>Nessuna risposta trovata per "{query}"</p>
          <p className="text-sm mt-1">Prova una parola diversa o contattaci.</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Accordion type="single" collapsible className="space-y-2">
      {filtered.map((f, i) => (
        <AccordionItem
          key={i}
          value={`item-${i}`}
          className="border rounded-xl overflow-hidden bg-card data-[state=open]:shadow-md transition-all"
        >
          <AccordionTrigger className="px-4 py-3 hover:no-underline text-left">
            <span className="font-semibold pr-4">{f.q}</span>
          </AccordionTrigger>
          <AccordionContent className="px-4 pb-4 pt-0 text-foreground/80 leading-relaxed">
            {f.a}
          </AccordionContent>
        </AccordionItem>
      ))}
    </Accordion>
  );
};

export const HelpPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialTab = searchParams.get('s') || 'players';
  const [tab, setTab] = useState(initialTab);
  const [query, setQuery] = useState('');

  const totalFaqs = useMemo(() => SECTIONS.reduce((acc, s) => acc + s.faqs.length, 0), []);
  const matchCount = useMemo(() => {
    if (!query) return totalFaqs;
    const q = query.toLowerCase();
    return SECTIONS.reduce((acc, s) =>
      acc + s.faqs.filter(f => f.q.toLowerCase().includes(q) || f.a.toLowerCase().includes(q)).length
    , 0);
  }, [query, totalFaqs]);

  const handleTabChange = (val) => {
    setTab(val);
    setSearchParams({ s: val });
  };

  return (
    <div className="safe-public-page" data-testid="help-page">
      <div className="pb-24">
      {/* Hero */}
      <div className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-blue-600 via-indigo-600 to-violet-600" />
        <div className="pointer-events-none absolute -top-32 -right-32 w-96 h-96 rounded-full bg-white/10 blur-3xl" />
        <div className="pointer-events-none absolute -bottom-32 -left-32 w-96 h-96 rounded-full bg-white/15 blur-3xl" />
        <div className="relative px-4 md:px-8 pt-8 pb-12 md:pt-12 md:pb-16">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-6">
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white/20 backdrop-blur text-white text-[11px] uppercase tracking-widest font-semibold mb-3">
                <HelpCircle className="h-3 w-3" />
                Centro Aiuto
              </span>
              <h1 className="text-3xl md:text-5xl font-bold text-white drop-shadow-lg">
                Come possiamo aiutarti?
              </h1>
              <p className="text-white/85 mt-2">
                Risposte rapide per giocatori, organizzatori e gestori di strutture.
              </p>
              <Button
                type="button"
                variant="secondary"
                size="sm"
                className="mt-4"
                onClick={showWhatsNew}
                data-testid="replay-whats-new"
              >
                <Sparkles className="h-4 w-4 mr-1.5" /> Rivedi le novità
              </Button>
            </div>

            <div className="relative">
              <div className="absolute inset-0 bg-white/30 rounded-2xl blur-xl" aria-hidden />
              <div className="relative bg-white dark:bg-slate-900 rounded-2xl shadow-2xl flex items-center pr-2">
                <Search className="ml-5 h-6 w-6 text-muted-foreground shrink-0" />
                <Input
                  placeholder="Cerca tra le domande frequenti..."
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  className="border-0 h-14 md:h-16 text-base md:text-lg shadow-none focus-visible:ring-0 bg-transparent"
                />
                <span className="text-xs text-muted-foreground tabular-nums shrink-0 px-3">
                  {matchCount}/{totalFaqs}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="relative z-10 max-w-5xl mx-auto px-4 md:px-8 -mt-6 space-y-6">
        {/* Section tabs */}
        <Tabs value={tab} onValueChange={handleTabChange} className="space-y-6">
          <Card className="overflow-hidden">
            <TabsList className="w-full grid grid-cols-3 h-auto p-1.5 bg-card gap-1">
              {SECTIONS.map(s => {
                const Icon = s.icon;
                return (
                  <TabsTrigger
                    key={s.id}
                    value={s.id}
                    className="flex flex-col md:flex-row gap-1 md:gap-2 py-3 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground"
                  >
                    <Icon className="h-4 w-4" />
                    <span className="text-xs md:text-sm font-semibold">{s.label}</span>
                  </TabsTrigger>
                );
              })}
            </TabsList>
          </Card>

          {SECTIONS.map(s => (
            <TabsContent key={s.id} value={s.id} className="space-y-4">
              <Card className={`overflow-hidden bg-gradient-to-br ${s.accent} text-white border-0`}>
                <CardContent className="p-5 flex items-center gap-4">
                  <div className="h-12 w-12 rounded-xl bg-white/20 backdrop-blur flex items-center justify-center shrink-0">
                    <s.icon className="h-6 w-6" />
                  </div>
                  <div>
                    <h2 className="font-bold text-lg leading-tight">{s.label}</h2>
                    <p className="text-sm text-white/85">{s.blurb}</p>
                  </div>
                </CardContent>
              </Card>

              <SectionTab section={s} query={query} />
            </TabsContent>
          ))}
        </Tabs>

        {/* Footer help */}
        <Card>
          <CardContent className="p-6 space-y-4">
            <div className="flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-primary" />
              <h3 className="font-bold">Non hai trovato quello che cercavi?</h3>
            </div>
            <p className="text-sm text-muted-foreground">
              Scrivici un feedback dall&apos;interno della app, oppure contattaci direttamente.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <QuickLink to="/announcements" icon={MessageSquare} label="Pubblica un annuncio" />
              <a href="mailto:supporto@leaguehub.it">
                <Card className="overflow-hidden hover:shadow-lg hover:-translate-y-0.5 transition-all">
                  <CardContent className="p-4 flex items-center gap-3">
                    <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-blue-500/15 to-indigo-500/15 flex items-center justify-center text-primary">
                      <Mail className="h-5 w-5" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium">supporto@leaguehub.it</p>
                      <p className="text-xs text-muted-foreground">Rispondiamo in 24h</p>
                    </div>
                  </CardContent>
                </Card>
              </a>
            </div>
            <div className="pt-2 border-t">
              <Button variant="outline" size="sm" asChild>
                <Link to="/dashboard">Torna alla home</Link>
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
      </div>
    </div>
  );
};

export default HelpPage;
